import { isRecord } from './parsingHelpers';

/** HTTP list-messages defaults to 40; page in chunks so clients get the full thread. */
export const MASTRA_THREAD_MESSAGES_PAGE_SIZE = 500;

const MASTRA_THREAD_MESSAGES_MAX_PAGES = 100;

export const MASTRA_THREAD_MESSAGES_ORDER_BY = {
    field: 'createdAt',
    direction: 'ASC',
} as const;

export interface ListMastraThreadMessagesPageArgs {
    page: number;
    perPage: number;
    orderBy: typeof MASTRA_THREAD_MESSAGES_ORDER_BY;
}

const readHasMore = (response: unknown): boolean | undefined => {
    if (!isRecord(response) || typeof response.hasMore !== 'boolean') {
        return undefined;
    }
    return response.hasMore;
};

/**
 * Walks Mastra's paginated `listMessages` API until every stored message is
 * returned. The HTTP route defaults to 40 (same as agent `lastMessages`) when
 * `perPage` is omitted, and it does not accept `perPage: false`.
 */
export const listAllMastraThreadMessages = async <TMessage>(
    listPage: (
        args: ListMastraThreadMessagesPageArgs,
    ) => Promise<{ messages: TMessage[] }>,
): Promise<TMessage[]> => {
    const messages: TMessage[] = [];

    for (let page = 0; page < MASTRA_THREAD_MESSAGES_MAX_PAGES; page += 1) {
        // Sequential pages so we can stop at the first short / hasMore=false page.
        // eslint-disable-next-line no-await-in-loop -- pagination is inherently serial
        const response = await listPage({
            page,
            perPage: MASTRA_THREAD_MESSAGES_PAGE_SIZE,
            orderBy: MASTRA_THREAD_MESSAGES_ORDER_BY,
        });
        const pageMessages = response.messages;
        messages.push(...pageMessages);

        const hasMore = readHasMore(response);
        const fetchedFullPage =
            pageMessages.length === MASTRA_THREAD_MESSAGES_PAGE_SIZE;
        if (
            pageMessages.length === 0 ||
            hasMore === false ||
            !fetchedFullPage
        ) {
            break;
        }
    }

    return messages;
};
